import { useMemo } from "react";
import type { ViewTagDataSet } from "../models/TrendingModelsV2";

export interface TagMinMax {
	[tagId: number]: {
		min: number;
		max: number;
	};
}

/**
 * Hook to calculate overall min/max values from series data
 * This is optimized for performance by only calculating once per series data change
 */
export const useSeriesMinMax = (
	series: ViewTagDataSet[][] | undefined,
	tagIds: number[],
): TagMinMax => {
	return useMemo(() => {
		const result: TagMinMax = {};

		if (!series || series.length === 0) {
			return result;
		}

		// Process each tag's series data
		series.forEach((tagSeries, index) => {
			const tagId = tagIds[index];
			if (!tagId || !tagSeries || tagSeries.length === 0) {
				return;
			}

			let min = Number.POSITIVE_INFINITY;
			let max = Number.NEGATIVE_INFINITY;

			// Find min/max values across all data points
			for (const point of tagSeries) {
				if (point.value !== null && point.value !== undefined) {
					if (point.value < min) min = point.value;
					if (point.value > max) max = point.value;
				}
			}

			// Only store if we found valid values
			if (
				min !== Number.POSITIVE_INFINITY &&
				max !== Number.NEGATIVE_INFINITY
			) {
				result[tagId] = { min, max };
			} else {
				// No valid data found
				result[tagId] = { min: 0, max: 0 };
			}
		});

		return result;
	}, [series, tagIds]);
};
